import { ExperimentValues } from "./Experiment";
import { UTMValues } from "./Tracking";
import { AdditionalMessageData } from "./User";
import { uuidv7 } from "./uuidv7";

export { TagManager, EventForMessages, resolveCustomGtmLoader, resolveEventId };
export type { GtmCustomLoaderEntry };

interface EventForMessages {
  event_name: string;
  event_page?: string;
}

interface GtmCustomLoaderEntry {
  enabled: boolean;
  script: string;
  noscript?: string;
}

/**
 * Resolves the custom GTM loader (Stape-like) for a hostname from the
 * gtm_custom_loaders business unit config value: a raw JSON string keyed by
 * marketing domain hostname, e.g.
 * {"go.example.com":{"enabled":true,"script":"<script>...</script>","noscript":"<noscript>...</noscript>"}}
 *
 * Any parsing or shape problem returns null so the standard Google loader is
 * used — a corrupted config value must never break tracking.
 */
function resolveCustomGtmLoader(
  rawJson: string | undefined,
  hostname: string,
): GtmCustomLoaderEntry | null {
  if (!rawJson) return null;

  try {
    const map = JSON.parse(rawJson) as Record<
      string,
      GtmCustomLoaderEntry | undefined
    > | null;
    const entry = map?.[hostname.toLowerCase()];

    if (
      entry?.enabled === true &&
      typeof entry.script === "string" &&
      entry.script.includes("<script")
    ) {
      return entry;
    }
  } catch {
    // Corrupted config value: fall back to the standard loader
  }

  return null;
}

/**
 * Identidad del evento que se publica en el dataLayer.
 *
 * Histórico: era un uuidv7 DESECHABLE por push, que no viajaba a ningún lado.
 * Desde los píxeles de navegador, cuando el emisor trae una identidad
 * canónica (`data.event_id`: la misma que va al píxel y al CAPI) se respeta —
 * así una etiqueta de GTM puede pasarla como Event ID y deduplicar contra
 * nuestro CAPI sin más cambios. Sin ella, se sigue acuñando una al vuelo.
 */
function resolveEventId(data: unknown): string {
  try {
    const candidate = (data as Record<string, unknown>)?.["event_id"];
    if (typeof candidate === "string" && candidate !== "") {
      return candidate;
    }
  } catch (e) {
    // un data raro nunca puede impedir el push al dataLayer
  }
  return uuidv7();
}

class TagManager {
  private _tagManagerId: string | undefined;
  private _customLoadersJson: string | undefined;

  constructor(
    tracking: UTMValues,
    user: AdditionalMessageData,
    userForm: any,
    experiment?: ExperimentValues,
    tagManagerId?: string,
    customLoadersJson?: string,
  ) {
    this._tagManagerId = tagManagerId;
    this._customLoadersJson = customLoadersJson;
    window.dataLayer = [{ ...tracking, ...user, ...userForm, ...experiment }];
    this.init();
    return this;
  }

  async init(): Promise<void> {
    const customLoader = resolveCustomGtmLoader(
      this._customLoadersJson,
      window.location.hostname,
    );

    if (customLoader) {
      this._injectCustomLoader(customLoader);
      return;
    }

    if (!this._tagManagerId) {
      // No container configured for this business unit: skip the standard
      // snippet instead of requesting gtm.js?id=undefined
      return;
    }

    (function (w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      // eslint-disable-next-line no-var
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != "dataLayer" ? "&l=" + l : "";
      // @ts-expect-error This not our code to fix.
      j.async = true;
      // @ts-expect-error This not our code to fix.
      j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, "script", "dataLayer", this._tagManagerId);
  }

  async sendEventForMessages(
    dataType: string,
    data: EventForMessages,
  ): Promise<void> {
    window.dataLayer.push({
      ...data,
      event: data.event_name,
      event_type: dataType,
      event_id: resolveEventId(data),
    });
  }

  async sendCustomEvent<T>(
    eventName: string,
    data: T = {} as T,
  ): Promise<void> {
    window.dataLayer.push({
      ...data,
      event: eventName,
      event_id: resolveEventId(data),
    });
  }

  /**
   * Injects the pasted Stape-like snippet. The dataLayer bootstrap done in the
   * constructor stays untouched: the custom snippet performs the same
   * w[l]=w[l]||[] + gtm.start push as the standard one, only the script host
   * changes.
   *
   * Scripts parsed via DOMParser are inert, so each <script> is recreated as a
   * real element (attributes + inline code) and inserted before the first
   * script of the document, mirroring what the standard GTM snippet does.
   *
   * The <noscript> is appended to <body> only for parity with the snippet the
   * provider hands out: injected via JS it can never render (browsers without
   * JS never run this code), so real no-JS coverage is out of scope here.
   */
  private _injectCustomLoader(entry: GtmCustomLoaderEntry): void {
    const parsedScript = new DOMParser().parseFromString(
      entry.script,
      "text/html",
    );
    const firstScript = document.getElementsByTagName("script")[0] ?? null;

    for (const inertScript of Array.from(
      parsedScript.querySelectorAll("script"),
    )) {
      const script = document.createElement("script");
      for (const attribute of Array.from(inertScript.attributes)) {
        script.setAttribute(attribute.name, attribute.value);
      }
      script.text = inertScript.textContent ?? "";

      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
    }

    if (entry.noscript && entry.noscript.includes("<noscript")) {
      const parsedNoscript = new DOMParser().parseFromString(
        entry.noscript,
        "text/html",
      );
      for (const noscript of Array.from(
        parsedNoscript.querySelectorAll("noscript"),
      )) {
        document.body.appendChild(document.importNode(noscript, true));
      }
    }
  }
}
